<template>
  <div>
    <div class="player-passport-buttons" v-if="userData.userType !== 'User' && selectedUserValue !== ''">
      <h3 class="passport-title">Player Passport</h3>
      <div class="player-passport">
        <div class="view-passport" v-b-tooltip.hover.top="'View Player Passport'"
          @click="openCreateAcademyReportModal()">
          <img id="passport-icon" src="./img/player-passport-icon.svg" alt="Player passport icon" />
          <span class="passport-caption">View the player<br> passport</span>
        </div>

        <b-button class="main-button create-academy-report-button" @click="editAcademyReportModal()" size="lg"
          variant="outline-warning" v-b-tooltip.hover.top="'Edit Player Passport'">
          <i class="bi bi-pencil" aria-hidden="true"></i></b-button>
      </div>
    </div>

    <!-- Edit Academy Report Modal -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-dialog modal-xl" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>Edit Player Passport</h3>
            <button type="button" class="btn-close" @click="closeEditModal">
              &times;
            </button>
          </div>
          <div class="modal-body">
            <div class="performance-target-modal-content">

              <!-- Player Details Override Section -->
              <div class="override-section">
                <h4 class="section-title">Player Details Override</h4>
                <p class="section-hint">Leave blank to use actual player data</p>
                <div class="form-grid">
                  <div class="form__item">
                    <label class="form__label" for="override_position">Position:</label>
                    <input type="text" id="override_position" v-model="academyReport.override_position"
                      :placeholder="selectedUserData.position || 'e.g. Midfielder'" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_age_group">Age Group:</label>
                    <input type="text" id="override_age_group" v-model="academyReport.override_age_group"
                      :placeholder="selectedUserData.age_group || 'e.g. U17'" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_dob">Date of Birth:</label>
                    <input type="date" id="override_dob" v-model="academyReport.override_dob" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_joined_date">Signed Date:</label>
                    <input type="date" id="override_joined_date" v-model="academyReport.override_joined_date"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_height">Height (cm):</label>
                    <input type="number" id="override_height" v-model="academyReport.override_height"
                      :placeholder="playerPhysicalPerformanceData.height || 'e.g. 175'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_weight">Weight (kg):</label>
                    <input type="number" id="override_weight" v-model="academyReport.override_weight"
                      :placeholder="playerPhysicalPerformanceData.weight || 'e.g. 70'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_nationality">Nationality:</label>
                    <input type="text" id="override_nationality" v-model="extraOverrides.nationality"
                      :placeholder="selectedUserData.nationality || 'e.g. Scotland'" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_second_nationality">Second nationality:</label>
                    <input type="text" id="override_second_nationality" v-model="extraOverrides.second_nationality"
                      :placeholder="selectedUserData.second_nationality || 'e.g. Ireland'" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_country_of_birth">Country of birth:</label>
                    <input type="text" id="override_country_of_birth" v-model="extraOverrides.country_of_birth"
                      :placeholder="selectedUserData.country_of_birth || 'e.g. Scotland'" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_playing_up_down">Playing up/down:</label>
                    <input type="text" id="override_playing_up_down" v-model="extraOverrides.playing_up_down"
                      :placeholder="playingUpDownCompact()" class="form-control" />
                  </div>
                </div>
              </div>

              <!-- Season Performance Override Section -->
              <div class="override-section">
                <h4 class="section-title">Season Performance Override</h4>
                <p class="section-hint">Leave blank to use calculated stats from match reports</p>
                <div class="form-grid">
                  <div class="form__item">
                    <label class="form__label" for="override_appearances">Appearances:</label>
                    <input type="number" id="override_appearances" v-model="academyReport.override_appearances"
                      :placeholder="gamePerformanceData.gamesPlayed || '0'" min="0" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_goals">Goals:</label>
                    <input type="number" id="override_goals" v-model="academyReport.override_goals"
                      :placeholder="gamePerformanceData.goals || '0'" min="0" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_assists">Assists:</label>
                    <input type="number" id="override_assists" v-model="academyReport.override_assists"
                      :placeholder="gamePerformanceData.assists || '0'" min="0" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_game_time">Game Time (mins):</label>
                    <input type="number" id="override_game_time" v-model="academyReport.override_game_time"
                      :placeholder="gamePerformanceData.timeOnPitch || '0'" min="0" class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_self_rating">Self Rating:</label>
                    <input type="number" id="override_self_rating" v-model="academyReport.override_self_rating"
                      :placeholder="gamePerformanceData.player_self_rating || 'e.g. 7.5'" min="0" max="10" step="0.1"
                      class="form-control" />
                  </div>
                </div>
              </div>

              <!-- Physical Performance Override Section -->
              <div class="override-section">
                <h4 class="section-title">Physical Performance Override</h4>
                <p class="section-hint">Leave blank to use the latest recorded test results</p>
                <div class="form-grid">
                  <div class="form__item">
                    <label class="form__label" for="override_tenm">10m Sprint (s):</label>
                    <input type="number" id="override_tenm" v-model="extraOverrides.tenm_sprint"
                      :placeholder="playerPhysicalPerformanceData.tenM_sprint_time || 'e.g. 1.85'" step="0.01"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_thirtym">30m Sprint (s):</label>
                    <input type="number" id="override_thirtym" v-model="extraOverrides.thirtym_sprint"
                      :placeholder="playerPhysicalPerformanceData.fortyM_sprint_time || 'e.g. 4.20'" step="0.01"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_cmj">Countermovement Jump (cm):</label>
                    <input type="number" id="override_cmj" v-model="extraOverrides.cmj"
                      :placeholder="playerPhysicalPerformanceData.cuntermovement_jump || 'e.g. 32.5'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_rsi">Reactive Strength Index:</label>
                    <input type="number" id="override_rsi" v-model="extraOverrides.rsi"
                      :placeholder="playerPhysicalPerformanceData.reactive_strength_index || 'e.g. 1.8'" step="0.01"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_fitness">Fitness Test (30-15):</label>
                    <input type="number" id="override_fitness" v-model="extraOverrides.fitness_test"
                      :placeholder="playerPhysicalPerformanceData.thirty_15_intermittent_fitness_test || 'e.g. 18.5'"
                      step="0.1" class="form-control" />
                  </div>
                </div>
              </div>

              <!-- Growth & Maturation Override Section -->
              <div class="override-section">
                <h4 class="section-title">Growth &amp; Maturation Override</h4>
                <p class="section-hint">Leave blank to use the Bio-banding calculator results</p>
                <div class="form-grid">
                  <div class="form__item">
                    <label class="form__label" for="override_chrono_age">Chronological age (yrs):</label>
                    <input type="number" id="override_chrono_age" v-model="extraOverrides.chronological_age"
                      :placeholder="(bioAgeData && bioAgeData.decimalAge) || 'e.g. 14.2'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_bio_age">Biological age (yrs):</label>
                    <input type="number" id="override_bio_age" v-model="extraOverrides.bio_age"
                      :placeholder="(bioAgeData && bioAgeData.bioAge) || 'e.g. 15.1'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_bio_diff">Difference (yrs):</label>
                    <input type="number" id="override_bio_diff" v-model="extraOverrides.bio_age_difference"
                      :placeholder="(bioAgeData && bioAgeData.bioAgeDifference) || 'e.g. 0.9'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_est_height">Estimated adult height (cm):</label>
                    <input type="number" id="override_est_height" v-model="extraOverrides.estimated_adult_height"
                      :placeholder="(bioAgeData && bioAgeData.childEstimatedHeight) || 'e.g. 182'" step="0.1"
                      class="form-control" />
                  </div>
                  <div class="form__item">
                    <label class="form__label" for="override_height_achieved">Adult height achieved (%):</label>
                    <input type="number" id="override_height_achieved" v-model="extraOverrides.adult_height_achieved"
                      :placeholder="(bioAgeData && bioAgeData.childEstimatedHeightAchieved) || 'e.g. 88'" step="0.1"
                      class="form-control" />
                  </div>
                </div>
              </div>

              <!-- Medical Information Override Section -->
              <div class="override-section">
                <h4 class="section-title">Medical Information Override</h4>
                <p class="section-hint">Leave blank to use the player's recorded medical information</p>
                <div class="form__item">
                  <textarea id="override_medical" v-model="extraOverrides.medical"
                    :placeholder="selectedUserData.medical || 'Any medical notes to show on the passport...'"
                    rows="3" class="form-control"></textarea>
                </div>
              </div>

              <!-- Coaches Comments Section -->
              <div class="override-section">
                <h4 class="section-title">Coaches Comments</h4>
                <div class="form__item">
                  <textarea id="coaches_comments" v-model="academyReport.coaches_comments"
                    placeholder="Enter your comments about the player's development, strengths, areas for improvement..."
                    rows="6" class="form-control"></textarea>
                </div>
              </div>

              <div class="buttons-row">
                <b-button size="lg" variant="outline-secondary" @click="clearOverrides()">
                  Clear Overrides</b-button>
                <b-button size="lg" variant="outline-warning" @click="updateTheAcademyReport(academyReport.id)">
                  Update Player Passport</b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Academy Report Modal -->
    <div v-if="showReportModal" class="modal-overlay modal-fullscreen" @click="closeReportModal">
      <div class="modal-dialog modal-xl" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h3>Player Passport</h3>
            <button type="button" class="btn-close" @click="closeReportModal">
              &times;
            </button>
          </div>
          <div class="modal-body">
            <div class="performance-target-modal-content">
              <div class="buttons-row">
                <b-button size="lg" variant="outline-success" @click="produceTheAcademyReport()"
                  :disabled="isGeneratingPdf">
                  <span v-if="isGeneratingPdf">
                    <i class="bi bi-hourglass-split spin-animation"></i> Generating...
                  </span>
                  <span v-else>Produce report</span>
                </b-button>
              </div>

              <div class="modal-container performance-report">
                <iframe v-if="showReport === true && pdfDataUri" id="theAcademyReportPdf_preview" :src="pdfDataUri" />
                <div v-else-if="isGeneratingPdf" class="loading-overlay">
                  <div class="loading-container">
                    <div class="loading-spinner"></div>
                    <div class="loading-status">{{ loadingStatus }}</div>
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{ width: loadingProgress + '%' }"></div>
                    </div>
                  </div>
                </div>
                <img v-else src="./img/football-illustration.jpg" alt="" />
              </div>
              <!-- Hidden images for PDF generation -->
              <img src="./img/pitch-potions.jpg" alt="" class="visually-hidden" id="pitch_potions" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { AcademyReport } from "@/services/academy-report-service.js";
import { PlayerPhysicalPerformance } from "@/services/player-physical-performance-service.js";
import { Teams } from "@/services/teams-service.js";
import { PlayerData } from "@/services/playerData-service.js";
import { playerDataApi } from "@/utils/playerDataBackend.js";
import tenantConfig from "@/config/tenant.js";
import extendSeasonEndDateToFullYearMixin from "@/mixin/extendSeasonEndDateToFullYear.js";
import JsPDF from "jspdf";
import moment from "moment";

export default {
  name: "AcademyReport",
  mixins: [extendSeasonEndDateToFullYearMixin],
  data() {
    return {
      showReport: false,
      showEditModal: false,
      showReportModal: false,
      academyReport: {
        id: null,
        player_id: null,
        seasion: null,
        average_score: 0,
        coaches_comments: "",
        // Player Details overrides
        override_dob: null,
        override_age_group: null,
        override_joined_date: null,
        override_height: null,
        override_weight: null,
        override_position: null,
        // Season Performance overrides
        override_appearances: null,
        override_goals: null,
        override_assists: null,
        override_game_time: null,
        override_self_rating: null,
        // JSON blob of the newer section overrides (see extraOverrides).
        override_extra: null,
      },
      // Overrides for the newer passport sections. Kept separate from
      // academyReport (which is wholesale-reassigned on populate) and
      // serialised into academyReport.override_extra on save.
      extraOverrides: {
        nationality: null,
        second_nationality: null,
        country_of_birth: null,
        playing_up_down: null,
        medical: null,
        tenm_sprint: null,
        thirtym_sprint: null,
        cmj: null,
        rsi: null,
        fitness_test: null,
        chronological_age: null,
        bio_age: null,
        bio_age_difference: null,
        estimated_adult_height: null,
        adult_height_achieved: null,
      },
      allPlayerPhysicalPerformanceData: [],
      playerPhysicalPerformanceData: {},
      // Growth & maturation (bio-banding) results for this player.
      bioAgeData: null,
      // Other teams the player features for, used for the playing up/down line.
      playersOtherTeams: [],
      // PlayerData GPS Summary
      playerDataGpsSummary: null,
      playerDataAthleteId: null,
      playerDataTeams: [],
      loadingGpsData: false,
      // Club branding
      clubLogoLoaded: null,
      tenantConfig: tenantConfig,
      // PDF preview
      pdfDataUri: null,
      // Loading state
      isGeneratingPdf: false,
      loadingStatus: '',
      loadingProgress: 0,
    };
  },
  props: {
    userData: {
      type: Object,
      default: () => { },
    },
    selectedUserValue: {
      type: Number,
      default: null,
    },
    gamePerformanceData: {
      type: Object,
      default: () => { },
    },
    selectedTeamId: {
      type: Number,
      default: null,
    },
    selectedUserData: {
      type: Object,
      default: () => { },
    },
    playerCvData: {
      type: Object,
      default: () => { },
    },
  },
  computed: {},
  mounted() {
    if (this.selectedUserValue !== null) {
      this.populateAcademyReport();
    }
  },
  methods: {
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    async loadPlayerPhysicalPerformanceData() {
      const userData = {
        player_id: this.selectedUserValue,
        action: "loadPlayerPhysicalPerformanceDataById",
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      };
      const responce = await PlayerPhysicalPerformance.find(userData).catch(
        (error) => {
          console.log(error);
        },
      );

      if (responce.data.length > 0) {
        this.allPlayerPhysicalPerformanceData = responce.data;
        this.playerPhysicalPerformanceData =
          this.getPlayerPhysicalPerformanceData();
      }
    },
    getPlayerPhysicalPerformanceData() {
      return this.allPlayerPhysicalPerformanceData.reduce((a, b) => {
        return new Date(a.date) > new Date(b.date) ? a : b;
      });
    },
    async loadBioAgeData() {
      try {
        const res = await PlayerPhysicalPerformance.getBioAgeResults(
          this.selectedUserValue,
        );
        const r = res && res.data;
        this.bioAgeData =
          r && typeof r === "object" && r.bioAge !== null && r.bioAge !== undefined
            ? r
            : null;
      } catch (e) {
        this.bioAgeData = null;
      }
    },
    async loadPlayersOtherTeams() {
      try {
        const res = await Teams.find({
          action: "getPlayersOtherTeams",
          user_id: this.selectedUserValue,
          defaultTeamId: this.selectedUserData && this.selectedUserData.teamId,
        });
        this.playersOtherTeams = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.playersOtherTeams = [];
      }
    },
    // age_group is a birth YEAR: a LOWER year = older players = playing up.
    playingLevelLabel(ageGroup) {
      const playerYear = parseInt(
        this.selectedUserData && this.selectedUserData.age_group,
        10,
      );
      const teamYear = parseInt(ageGroup, 10);
      if (isNaN(playerYear) || isNaN(teamYear)) return "Also playing";
      if (teamYear < playerYear) return "up";
      if (teamYear > playerYear) return "down";
      return "level";
    },
    // Compact label for the PLAYER DETAILS box.
    playingUpDownCompact() {
      const teams = this.playersOtherTeams || [];
      if (!teams.length) return "No";
      const levels = new Set(
        teams.map((t) => this.playingLevelLabel(t.age_group)),
      );
      const up = levels.has("up");
      const down = levels.has("down");
      if (up && down) return "Up & down";
      if (up) return "Up";
      if (down) return "Down";
      return "Same age";
    },
    // Fuller list (each other team + direction) for the positions section.
    playingUpDownSummary() {
      const teams = this.playersOtherTeams || [];
      if (!teams.length) return "Primary age group only";
      return teams
        .map((t) => {
          const level = this.playingLevelLabel(t.age_group);
          const name = t.teamName || t.age_group || "Team";
          if (level === "up") return `${name} (up)`;
          if (level === "down") return `${name} (down)`;
          return `${name} (same age)`;
        })
        .join(", ");
    },
    addPlayingPostions(pdf, pitchX, pitchY, pitchWidth, pitchHeight) {
      pdf.setFontSize(8).setFont(undefined, "bold");

      // Count the occurrences of each position
      const positionCounts = this.gamePerformanceData.postion_played.reduce(
        (counts, position) => {
          counts[position] = (counts[position] || 0) + 1;
          return counts;
        },
        {},
      );

      // Scale factors - original coordinates based on 80x120 pitch
      const scaleX = pitchWidth / 80;
      const scaleY = pitchHeight / 120;

      // Define positions with coordinates relative to the pitch
      // These are based on the original 80x120 pitch layout from profile view
      const positions = {
        Goalkeeper: { x: 42, y: 111, label: "GK" },
        "Right Full-back (Wingback)": { x: 70, y: 94, label: "RB" },
        "Center-back left": { x: 31, y: 94, label: "CB" },
        "Center-back right": { x: 51, y: 94, label: "CB" },
        "Defensive Midfielder": { x: 40, y: 78, label: "DM" },
        "Attacking Midfielder": { x: 40, y: 43, label: "AM" },
        "Central Midfielder": { x: 40, y: 61, label: "CM" },
        "Right Midfielder (Winger)": { x: 70, y: 61, label: "RW" },
        "Left Midfielder (Winger)": { x: 11, y: 61, label: "LW" },
        "Center Forward (Striker) left": { x: 25, y: 29, label: "CF" },
        "Center Forward (Striker) right": { x: 59, y: 29, label: "CF" },
        "Left Full-back (Wingback)": { x: 11, y: 94, label: "LB" },
      };

      // Loop through each position and add it to the PDF
      Object.keys(positions).forEach((position) => {
        const count = positionCounts[position] || 0;
        const pos = positions[position];

        // Calculate actual position on the scaled pitch
        const x = pitchX + (pos.x * scaleX);
        const y = pitchY + (pos.y * scaleY);

        // Assign colors based on the count
        if (count === 0) {
          pdf.setTextColor(180, 180, 180); // Gray for no appearances
        } else if (count <= 2) {
          pdf.setTextColor(255, 140, 0); // Orange for low numbers
        } else {
          pdf.setTextColor(0, 150, 0); // Green for higher numbers
        }

        // Add the position label
        pdf.text(pos.label, x, y, { align: 'center' });
      });

      // Reset text color
      pdf.setTextColor(51, 51, 51);
    },
    addPlayerVideos(pdf, height) {
      pdf.setFontSize(10).setFont(undefined, "normal");
      height = Math.round(height);

      const pageHeight = 297; // A4 page height in mm
      const topMargin = 10; // Top margin for new pages
      const videoIconHeight = 10; // Height of the video icon
      const lineSpacing = 10; // Spacing between video entries

      if (this.playerCvData.videos.length === 0) {
        pdf.text(5, height, "No videos available for this player.");
        return height + lineSpacing; // Return the height after adding the message
      } else {
        this.playerCvData.videos.forEach((item) => {
          let videoIcon = document.getElementById("video-icon");

          // Check if adding the next video will exceed the page height
          if (height + videoIconHeight > pageHeight - topMargin) {
            pdf.addPage(); // Add a new page
            height = topMargin; // Reset height to the top margin of the new page
          }

          // Add video icon and link
          pdf.addImage(videoIcon, "PNG", 5, height - 7, 10, 10);
          pdf.textWithLink(item.video_name, 20, height, {
            url: item.video_url,
          });

          // Increment height for the next video
          height += lineSpacing;
        });
      }

      return height;
    },
    openCreateAcademyReportModal() {
      this.showReportModal = true;
    },
    editAcademyReportModal() {
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    closeReportModal() {
      this.showReportModal = false;
      this.isGeneratingPdf = false;
      this.loadingStatus = '';
      this.loadingProgress = 0;
    },
    // --- Additional passport PDF sections -----------------------------------
    sectionHeader(pdf, y, margin, contentWidth, clubColor, title) {
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.text(margin + 5, y + 6, title);
      pdf.setTextColor(51, 51, 51);
      pdf.setFontSize(10).setFont(undefined, "normal");
      return y + 14;
    },
    addMedicalSection(pdf, y, margin, contentWidth, clubColor, pageHeight) {
      if (y > pageHeight - 40) {
        pdf.addPage();
        y = margin + 10;
      }
      y = this.sectionHeader(pdf, y, margin, contentWidth, clubColor, "MEDICAL INFORMATION");
      const medical = (this.getEffectiveMedical() || "").toString().trim();
      const text =
        !medical || medical.toUpperCase() === "N/A"
          ? "No medical information recorded."
          : medical;
      const lines = pdf.splitTextToSize(text, contentWidth - 10);
      pdf.text(margin + 5, y, lines);
      return y + lines.length * 5 + 8;
    },
    addPositionsPlayedSection(pdf, y, margin, contentWidth, clubColor, pageHeight) {
      if (y > pageHeight - 45) {
        pdf.addPage();
        y = margin + 10;
      }
      y = this.sectionHeader(pdf, y, margin, contentWidth, clubColor, "POSITIONS PLAYED BY GAMES");

      const played = Array.isArray(this.gamePerformanceData?.postion_played)
        ? this.gamePerformanceData.postion_played
        : [];
      const counts = {};
      played.forEach((p) => {
        if (p) counts[p] = (counts[p] || 0) + 1;
      });
      const ranked = Object.entries(counts)
        .map(([position, count]) => ({ position, count }))
        .sort((a, b) => b.count - a.count);

      if (!ranked.length) {
        pdf.text(margin + 5, y, "No game positions recorded yet.");
        return y + 10;
      }

      const maxCount = ranked[0].count;
      ranked.forEach((p, i) => {
        if (y > pageHeight - 18) {
          pdf.addPage();
          y = margin + 10;
        }
        pdf.setFont(undefined, "bold");
        pdf.text(margin + 5, y, `${i + 1}. ${p.position}`);
        pdf.setFont(undefined, "normal");
        pdf.text(
          margin + contentWidth - 5,
          y,
          `${p.count} ${p.count === 1 ? "game" : "games"}`,
          { align: "right" },
        );
        const barY = y + 2;
        pdf.setFillColor(238, 241, 244);
        pdf.rect(margin + 5, barY, contentWidth - 10, 2.5, "F");
        pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
        pdf.rect(margin + 5, barY, (contentWidth - 10) * (p.count / maxCount), 2.5, "F");
        y += 9;
      });

      // Age-group movement (playing up / down) detail.
      y += 1;
      pdf.setFontSize(9).setFont(undefined, "italic");
      pdf.setTextColor(90, 90, 90);
      pdf.text(margin + 5, y, `Age-group movement: ${this.playingUpDownSummary()}`);
      pdf.setFont(undefined, "normal");
      pdf.setTextColor(51, 51, 51);
      pdf.setFontSize(10);
      return y + 8;
    },
    addGrowthMaturationSection(pdf, y, margin, contentWidth, clubColor, pageHeight) {
      if (y > pageHeight - 55) {
        pdf.addPage();
        y = margin + 10;
      }
      y = this.sectionHeader(pdf, y, margin, contentWidth, clubColor, "GROWTH & MATURATION");

      const b = this.getEffectiveGrowth();
      if (!b) {
        pdf.text(margin + 5, y, "Not yet calculated in the Bio-banding calculator.");
        return y + 10;
      }
      const num = (v, dp = 1) => {
        const n = Number(v);
        return isNaN(n) ? "N/A" : n.toFixed(dp);
      };
      const signed = (v, dp = 1) => {
        const n = Number(v);
        if (isNaN(n)) return "N/A";
        return (n >= 0 ? "+" : "") + n.toFixed(dp);
      };
      const rows = [
        ["Chronological age", `${num(b.decimalAge)} yrs`],
        ["Biological age", `${num(b.bioAge)} yrs`],
        ["Difference", `${signed(b.bioAgeDifference)} yrs`],
        ["Estimated adult height", `${num(b.childEstimatedHeight)} cm`],
        ["Adult height achieved", `${num(b.childEstimatedHeightAchieved)} %`],
      ];
      rows.forEach(([label, value]) => {
        if (y > pageHeight - 18) {
          pdf.addPage();
          y = margin + 10;
        }
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin + 5, y - 4, contentWidth - 10, 7, "F");
        pdf.text(margin + 8, y + 1, label);
        pdf.setFont(undefined, "bold");
        pdf.text(margin + contentWidth - 8, y + 1, value, { align: "right" });
        pdf.setFont(undefined, "normal");
        y += 9;
      });
      return y + 4;
    },
    async produceTheAcademyReport() {
      this.showReport = true;
      this.pdfDataUri = null; // Reset PDF
      this.isGeneratingPdf = true;
      this.loadingStatus = 'Loading player data...';
      this.loadingProgress = 10;
      this.academyReport.player_id = this.selectedUserValue;
      this.academyReport.seasion = "2023";

      // Load physical performance data
      this.loadingStatus = 'Loading physical performance data...';
      this.loadingProgress = 20;
      await this.loadPlayerPhysicalPerformanceData();

      // Growth & maturation + other-team (playing up/down) data
      this.loadingStatus = 'Loading growth & maturation data...';
      this.loadingProgress = 25;
      await Promise.all([this.loadBioAgeData(), this.loadPlayersOtherTeams()]);

      // Load GPS data (this can take a while)
      this.loadingStatus = 'Loading GPS data...';
      this.loadingProgress = 30;
      await this.loadPlayerDataGpsSummary();

      // Load club branding
      this.loadingStatus = 'Loading club branding...';
      this.loadingProgress = 70;
      await this.loadClubLogo();

      // Wait for Vue to render after showReport = true
      await this.$nextTick();

      // Additional delay for images to load
      this.loadingStatus = 'Generating PDF...';
      this.loadingProgress = 80;

      setTimeout(async () => {
        try {
          this.loadingStatus = 'Building PDF document...';
          this.loadingProgress = 90;

          let pdf = this.buildTheAcademyReportPDF();
          if (pdf && pdf.pdfData) {
            this.loadingStatus = 'Finalizing...';
            this.loadingProgress = 95;

            // Use blob URL instead of data URI for large PDFs
            const pdfBlob = pdf.pdfData.output("blob");
            const blobUrl = URL.createObjectURL(pdfBlob);
            this.pdfDataUri = blobUrl;
            this.loadingProgress = 100;
            await this.$nextTick();
          } else {
            console.error('PDF generation returned invalid result:', pdf);
            this.loadingStatus = 'Error generating PDF';
          }
        } catch (error) {
          console.error('Error generating PDF:', error);
          this.loadingStatus = 'Error: ' + error.message;
        } finally {
          this.isGeneratingPdf = false;
        }
      }, 500);
    },

    /**
     * Load club logo as base64 for PDF embedding
     * Uses the existing logo from the header (TenantLogo component)
     */
    async loadClubLogo() {
      console.log('Loading club logo from header element...');

      // Get the logo from the header - it's already loaded by TenantLogo component
      const headerLogo = document.getElementById('main-logo');

      if (!headerLogo) {
        console.warn('Header logo element #main-logo not found');
        return null;
      }

      console.log('Found header logo:', headerLogo.src);

      // Wait for the image to be fully loaded
      return new Promise((resolve) => {
        const processLogo = () => {
          try {
            // Use fixed dimensions for the PDF
            const width = 150;
            const height = 150;

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            // Clear with transparent background
            ctx.clearRect(0, 0, width, height);

            // Draw the logo maintaining aspect ratio
            const imgWidth = headerLogo.naturalWidth || headerLogo.width || width;
            const imgHeight = headerLogo.naturalHeight || headerLogo.height || height;
            const scale = Math.min(width / imgWidth, height / imgHeight);
            const scaledWidth = imgWidth * scale;
            const scaledHeight = imgHeight * scale;
            const offsetX = (width - scaledWidth) / 2;
            const offsetY = (height - scaledHeight) / 2;

            ctx.drawImage(headerLogo, offsetX, offsetY, scaledWidth, scaledHeight);
            this.clubLogoLoaded = canvas.toDataURL('image/png');
            console.log('Club logo loaded from header successfully');
            resolve(this.clubLogoLoaded);
          } catch (e) {
            console.warn('Failed to convert header logo to canvas:', e);
            this.clubLogoLoaded = null;
            resolve(null);
          }
        };

        // Check if image is already loaded
        if (headerLogo.complete && headerLogo.naturalWidth > 0) {
          processLogo();
        } else {
          // Wait for it to load
          headerLogo.onload = processLogo;
          headerLogo.onerror = () => {
            console.warn('Header logo failed to load');
            this.clubLogoLoaded = null;
            resolve(null);
          };
        }
      });
    },

    /**
     * Get club primary color (returns RGB object)
     */
    getClubColor() {
      // Define club colors by tenant ID for reliability
      const clubColors = {
        'ptfc': '#eb2144',      // Partick Thistle - Red
        'kfc': '#0033A0',       // Kilmarnock - Blue
        'dfc': '#1C2C5B',       // Dundee - Dark Blue
        'stjfc': '#000000',     // St Johnstone - Black
        'ffc': '#FF0000',       // Falkirk - Red
        'club2': '#333333',     // Default
      };

      // Get color from tenant-specific mapping first
      const tenantId = this.tenantConfig?.id;
      let themeColor = clubColors[tenantId];

      // If not in mapping, try CSS custom property
      if (!themeColor) {
        const root = document.documentElement;
        const cssVar = getComputedStyle(root).getPropertyValue('--primary-color').trim();
        if (cssVar && cssVar.startsWith('#')) {
          themeColor = cssVar;
        }
      }

      // Final fallback
      if (!themeColor) {
        themeColor = '#eb2144';
      }

      console.log('Club color for tenant', tenantId, ':', themeColor);

      // Convert hex to RGB
      const hex = themeColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { r, g, b };
    },

    /**
     * Load PlayerData GPS summary for the player (PTFC only)
     */
    async loadPlayerDataGpsSummary() {
      console.log('loadPlayerDataGpsSummary called');
      console.log('userData:', this.userData);
      console.log('clubId:', this.userData?.clubId);

      // Only available for PTFC club
      if (this.userData?.clubId !== 'ptfc-2085XH') {
        console.log('Not PTFC club, skipping GPS data');
        return;
      }

      this.loadingGpsData = true;
      this.playerDataGpsSummary = null;

      try {
        // Get teams from local database
        if (this.playerDataTeams.length === 0) {
          console.log('Fetching teams from local database...');
          const teamsResponse = await PlayerData.find({
            playerId: this.userData.id,
            action: "getAllClubTeamsFromPlayerData",
          });
          console.log('Teams response:', teamsResponse);

          if (teamsResponse.data?.data?.length > 0) {
            this.playerDataTeams = teamsResponse.data.data;
            console.log('Teams found:', this.playerDataTeams);
          } else {
            console.warn('No PlayerData teams found');
            this.loadingGpsData = false;
            return;
          }
        }

        // Search through each team to find the athlete
        const playerName = this.selectedUserData.name;
        let athleteId = null;
        let foundInTeam = null;

        for (const team of this.playerDataTeams) {
          try {
            const sessionsResponse = await playerDataApi.getTeamSessions(team.id);

            if (sessionsResponse.success && sessionsResponse.data?.team?.sessions?.length > 0) {
              // Search through multiple sessions (not just the most recent) to find the athlete
              // Player might not be in the most recent session
              const sessions = sessionsResponse.data.team.sessions.slice(0, 10); // Check last 10 sessions

              for (const session of sessions) {
                const metricsResponse = await playerDataApi.getAthleteMetrics(session.id);

                if (metricsResponse.success && metricsResponse.data?.participations) {
                  const athlete = this.findAthleteByFuzzyName(
                    metricsResponse.data.participations,
                    playerName
                  );

                  if (athlete) {
                    athleteId = athlete.athlete?.id;
                    foundInTeam = team;
                    break;
                  }
                }
              }

              if (athleteId) break; // Found athlete, stop searching teams
            }
          } catch (err) {
            console.warn('Error searching team:', team.name, err);
          }
        }

        if (!athleteId) {
          this.loadingGpsData = false;
          return;
        }

        this.playerDataAthleteId = athleteId;

        // First try: Get historical data from local database (may have been synced from GpsReports)
        const historyResponse = await playerDataApi.getAthleteHistoricalData(athleteId, 50);

        let historicalData = [];
        if (historyResponse && Array.isArray(historyResponse)) {
          historicalData = historyResponse;
        } else if (historyResponse?.data && Array.isArray(historyResponse.data)) {
          historicalData = historyResponse.data;
        }

        // If no data in database, fetch directly from API sessions
        if (historicalData.length === 0 && foundInTeam) {
          historicalData = await this.fetchAthleteMetricsFromSessions(foundInTeam.id, athleteId);
        }

        if (historicalData.length > 0) {
          this.playerDataGpsSummary = this.calculateGpsSummary(historicalData);
        }
      } catch (error) {
        console.error('Error loading PlayerData GPS summary:', error);
      }

      this.loadingGpsData = false;
      console.log('Final playerDataGpsSummary:', this.playerDataGpsSummary);
    },

    /**
     * Fetch athlete metrics directly from team sessions (fallback when DB sync fails)
     */
    async fetchAthleteMetricsFromSessions(teamId, athleteId) {
      const athleteData = [];

      try {
        const sessionsResponse = await playerDataApi.getTeamSessions(teamId);

        if (!sessionsResponse.success || !sessionsResponse.data?.team?.sessions) {
          return athleteData;
        }

        const sessions = sessionsResponse.data.team.sessions.slice(0, 30); // Limit to last 30 sessions
        console.log(`Fetching metrics for ${sessions.length} sessions...`);

        for (let i = 0; i < sessions.length; i++) {
          const session = sessions[i];
          this.loadingStatus = `Loading GPS data... (${i + 1}/${sessions.length} sessions)`;
          try {
            const metricsResponse = await playerDataApi.getAthleteMetrics(session.id);

            if (metricsResponse.success && metricsResponse.data?.participations) {
              // Find this athlete in the session
              const participation = metricsResponse.data.participations.find(
                p => p.athlete?.id === athleteId
              );

              // Handle both Match (metricSet) and Training (athleteMetricSet) sessions
              const metrics = participation?.metricSet || participation?.athleteMetricSet;

              if (participation && metrics) {
                athleteData.push({
                  session_id: session.id,
                  session_date: session.startTime,
                  session_type: session.__typename,
                  total_distance_m: metrics.totalDistanceM || 0,
                  max_speed_kph: metrics.maxSpeedKph || 0,
                  avg_speed_kph: metrics.avgSpeedKph || 0,
                  high_intensity_distance_m: metrics.totalHighIntensityDistanceM || 0,
                  metres_per_minute: metrics.metresPerMinute || 0,
                  total_sprint_distance_m: metrics.totalSprintDistanceM || 0,
                  sprint_events: metrics.sprintEvents || 0,
                  acceleration_events: metrics.accelerationEvents || 0,
                  deceleration_events: metrics.decelerationEvents || 0,
                });
              }
            }
          } catch (err) {
            console.warn('Error fetching metrics for session:', session.id, err);
          }
        }
      } catch (error) {
        console.error('Error fetching sessions:', error);
      }

      return athleteData;
    },

    /**
     * Find athlete by fuzzy name matching
     */
    findAthleteByFuzzyName(participations, targetName) {
      if (!participations || !targetName) return null;

      const normalizedTarget = targetName.toLowerCase().trim();
      const targetParts = normalizedTarget.split(/\s+/);

      // Try exact match first
      let match = participations.find(p =>
        p.athlete?.name?.toLowerCase().trim() === normalizedTarget
      );
      if (match) {
        return match;
      }

      // Try matching last name (most reliable for sports contexts)
      const lastName = targetParts[targetParts.length - 1];
      if (lastName && lastName.length > 2) {
        match = participations.find(p => {
          const athleteName = p.athlete?.name?.toLowerCase().trim() || '';
          const athleteParts = athleteName.split(/\s+/);
          const athleteLastName = athleteParts[athleteParts.length - 1];
          return athleteLastName === lastName;
        });
        if (match) {
          return match;
        }
      }

      // Try matching first name + last name in any order
      match = participations.find(p => {
        const athleteName = p.athlete?.name?.toLowerCase().trim() || '';
        const athleteParts = athleteName.split(/\s+/);
        return targetParts.every(part =>
          athleteParts.some(ap => ap.includes(part) || part.includes(ap))
        );
      });
      if (match) {
        return match;
      }

      // Try partial match on any significant name part
      match = participations.find(p => {
        const athleteName = p.athlete?.name?.toLowerCase().trim() || '';
        return targetParts.some(part => athleteName.includes(part) && part.length > 2);
      });
      return match;
    },

    /**
     * Calculate GPS summary statistics (average and best) broken down by session type
     */
    calculateGpsSummary(historicalData) {
      // Separate sessions by type
      const matchSessions = historicalData.filter(s =>
        s.session_type === 'MatchSession' || s.session_type === 'Match'
      );
      const trainingSessions = historicalData.filter(s =>
        s.session_type === 'TrainingSession' || s.session_type === 'Training' || s.session_type === 'Session'
      );

      // Calculate metrics for all sessions combined, matches only, and training only
      const allMetrics = this.calculateMetricsForSessions(historicalData);
      const matchMetrics = this.calculateMetricsForSessions(matchSessions);
      const trainingMetrics = this.calculateMetricsForSessions(trainingSessions);

      return {
        // Overall counts
        sessionsCount: historicalData.length,
        matchSessionsCount: matchSessions.length,
        trainingSessionsCount: trainingSessions.length,
        // Combined metrics (for backward compatibility)
        ...allMetrics,
        // Separated metrics
        match: matchMetrics,
        training: trainingMetrics,
      };
    },

    /**
     * Calculate metrics for a set of sessions
     */
    calculateMetricsForSessions(sessions) {
      const metrics = {
        totalDistance: { values: [], unit: 'm' },
        maxSpeed: { values: [], unit: 'km/h' },
        sprintEvents: { values: [], unit: '' },
        highIntensityDistance: { values: [], unit: 'm' },
        accelerationEvents: { values: [], unit: '' },
        decelerationEvents: { values: [], unit: '' },
        sprintDistance: { values: [], unit: 'm' },
        metresPerMinute: { values: [], unit: 'm/min' },
        sessionDuration: { values: [], unit: 'mins' },
      };

      sessions.forEach(session => {
        const totalDist = parseFloat(session.total_distance_m) || 0;
        const maxSpd = parseFloat(session.max_speed_kph) || 0;
        const sprints = parseInt(session.sprint_events) || 0;
        const hiDist = parseFloat(session.high_intensity_distance_m) || 0;
        const accel = parseInt(session.acceleration_events) || 0;
        const decel = parseInt(session.deceleration_events) || 0;
        const sprintDist = parseFloat(session.total_sprint_distance_m) || 0;
        const mpm = parseFloat(session.metres_per_minute) || 0;

        // Only include non-zero values
        if (totalDist > 0) metrics.totalDistance.values.push(totalDist);
        if (maxSpd > 0) metrics.maxSpeed.values.push(maxSpd);
        if (sprints > 0) metrics.sprintEvents.values.push(sprints);
        if (hiDist > 0) metrics.highIntensityDistance.values.push(hiDist);
        if (accel > 0) metrics.accelerationEvents.values.push(accel);
        if (decel > 0) metrics.decelerationEvents.values.push(decel);
        if (sprintDist > 0) metrics.sprintDistance.values.push(sprintDist);
        if (mpm > 0) {
          metrics.metresPerMinute.values.push(mpm);
          // Calculate session duration: totalDistance / metresPerMinute
          if (totalDist > 0) {
            const duration = totalDist / mpm;
            metrics.sessionDuration.values.push(duration);
          }
        }
      });

      // Calculate average and best for each metric
      const result = {
        sessionsCount: sessions.length,
      };

      Object.keys(metrics).forEach(key => {
        const vals = metrics[key].values;
        if (vals.length > 0) {
          const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
          const best = Math.max(...vals);
          result[key] = {
            average: avg,
            best: best,
            unit: metrics[key].unit,
            count: vals.length,
          };
        } else {
          result[key] = null;
        }
      });

      return result;
    },

    /**
     * Format distance for display (m or km)
     */
    formatGpsDistance(meters) {
      if (!meters) return 'N/A';
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)}km`;
      }
      return `${Math.round(meters)}m`;
    },

    /**
     * Format distance for display - short version for compact tables
     */
    formatGpsDistanceShort(meters) {
      if (!meters) return '-';
      if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)}k`;
      }
      return `${Math.round(meters)}`;
    },

    addParagraphsToPDF(pdf, text, x, y, maxWidth, lineHeight, fontSize = 12) {
      // Split the text into paragraphs based on a character length of 300, ensuring no words are cut off
      const paragraphs = [];
      let currentParagraph = "";

      text.split(" ").forEach((word) => {
        if ((currentParagraph + word).length > 300) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = word + " ";
        } else {
          currentParagraph += word + " ";
        }
      });

      // Add the last paragraph if it exists
      if (currentParagraph.trim().length > 0) {
        paragraphs.push(currentParagraph.trim());
      }

      console.log("Paragraphs:", paragraphs);
      // Initialize the current height
      let currentHeight = {
        h: y,
      };

      // Set the font size for the paragraphs
      pdf.setFontSize(fontSize).setFont(undefined, "normal");

      // Loop through each paragraph
      paragraphs.forEach((paragraph) => {
        // Add the lines to the PDF
        pdf.text(x, currentHeight.h, paragraph, { maxWidth: maxWidth });

        // Calculate the height of the paragraph and update the current height
        const paragraphHeight = pdf.getTextDimensions(paragraph).h;
        currentHeight.h += paragraphHeight + lineHeight; // Add lineHeight for spacing between paragraphs
      });
      // Return the updated height
      return currentHeight;
    },
    buildTheAcademyReportPDF() {
      console.log('buildTheAcademyReportPDF called');
      let previewContainer = document.getElementById(
        "theAcademyReportPdf_preview",
      );
      let img = document.querySelector("#user_photo");
      let pdf = new JsPDF("p", "mm", "a4");

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);

      // Get club branding colors
      const clubColor = this.getClubColor();
      console.log('Club color:', clubColor);
      const clubName = this.tenantConfig.nameShort || this.tenantConfig.name || 'Club';

      // ========== PAGE 1: Header & Player Info ==========

      // Header background with club color
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(0, 0, pageWidth, 50, "F");

      // Add club logo in top right corner
      if (this.clubLogoLoaded) {
        try {
          pdf.addImage(this.clubLogoLoaded, "PNG", pageWidth - margin - 30, 5, 25, 25);
        } catch (e) {
          console.warn('Failed to add club logo to PDF:', e);
        }
      }

      // Player photo (circular)
      const diameter = 35;
      const scaleFactor = 4;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = diameter * scaleFactor;
      canvas.height = diameter * scaleFactor;
      ctx.beginPath();
      ctx.arc(
        (diameter * scaleFactor) / 2,
        (diameter * scaleFactor) / 2,
        (diameter * scaleFactor) / 2,
        0,
        Math.PI * 2,
      );
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, 0, 0, diameter * scaleFactor, diameter * scaleFactor);
      const clippedImage = canvas.toDataURL("image/png");
      pdf.addImage(clippedImage, "PNG", margin, 7, diameter, diameter);
      pdf.setDrawColor(255, 255, 255);
      pdf.setLineWidth(2);
      pdf.circle(margin + diameter / 2, 7 + diameter / 2, diameter / 2, "S");

      // Player name and title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22).setFont(undefined, "bold");
      pdf.text(margin + diameter + 10, 22, this.selectedUserData.name);
      pdf.setFontSize(12).setFont(undefined, "normal");
      pdf.text(margin + diameter + 10, 32, `${clubName} - Player Passport`);

      // Get effective values (override or actual)
      const effectiveDetails = this.getEffectivePlayerDetails();
      const effectivePerformance = this.getEffectiveSeasonPerformance();
      const effectiveIdentity = this.getEffectiveIdentity();
      const effectivePhysical = this.getEffectivePhysical();

      pdf.text(margin + diameter + 10, 40, `${effectiveDetails.position || 'Position N/A'} | Season 2024/25`);

      // Reset text color
      pdf.setTextColor(51, 51, 51);

      // ========== Player Details Section ==========
      let y = 60;

      // Three-column details panel.
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, y - 5, contentWidth, 35, "F");

      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.setTextColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.text(margin + 5, y + 3, "PLAYER DETAILS");

      pdf.setFontSize(8).setFont(undefined, "normal");
      pdf.setTextColor(51, 51, 51);

      const naDash = (v) =>
        v === undefined || v === null || v === "" ? "N/A" : v;
      const colA = margin + 5;
      const colB = margin + 68;
      const colC = margin + 132;
      const row1 = y + 11;
      const row2 = y + 18;
      const row3 = y + 25;

      // Column A — identity & dates
      pdf.text(colA, row1, `DOB: ${this.formatDateForHuman(effectiveDetails.dob)}`);
      pdf.text(colA, row2, `Age Group: ${naDash(effectiveDetails.age_group)}`);
      pdf.text(colA, row3, `Signed: ${this.formatDateForHuman(effectiveDetails.joined_date)}`);

      // Column B — nationality & birth
      pdf.text(colB, row1, `Nationality: ${naDash(effectiveIdentity.nationality)}`);
      pdf.text(colB, row2, `2nd nationality: ${naDash(effectiveIdentity.second_nationality)}`);
      pdf.text(colB, row3, `Birth country: ${naDash(effectiveIdentity.country_of_birth)}`);

      // Column C — physical & age-group movement
      pdf.text(colC, row1, `Height: ${naDash(effectiveDetails.height)} cm`);
      pdf.text(colC, row2, `Weight: ${naDash(effectiveDetails.weight)} kg`);
      pdf.text(colC, row3, `Playing up/down: ${naDash(effectiveIdentity.playing_up_down)}`);

      y += 40;

      // ========== Season Performance Section ==========
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.text(margin + 5, y + 6, "SEASON PERFORMANCE");

      y += 12;
      pdf.setTextColor(51, 51, 51);

      // Performance stats in grid - use effective values (override or actual)
      const statsY = y;
      const statBoxWidth = (contentWidth - 20) / 5;
      const stats = [
        { label: 'Appearances', value: effectivePerformance.appearances },
        { label: 'Goals', value: effectivePerformance.goals },
        { label: 'Assists', value: effectivePerformance.assists },
        { label: 'Game Time', value: `${effectivePerformance.gameTime} mins` },
        { label: 'Self Rating', value: effectivePerformance.selfRating },
      ];

      stats.forEach((stat, i) => {
        const x = margin + 5 + (i * statBoxWidth);
        pdf.setFillColor(241, 246, 249);
        pdf.rect(x, statsY, statBoxWidth - 5, 20, "F");

        pdf.setFontSize(16).setFont(undefined, "bold");
        pdf.setTextColor(clubColor.r, clubColor.g, clubColor.b);
        pdf.text(x + (statBoxWidth - 5) / 2, statsY + 10, `${stat.value}`, { align: 'center' });

        pdf.setFontSize(7).setFont(undefined, "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(x + (statBoxWidth - 5) / 2, statsY + 16, stat.label, { align: 'center' });
      });

      y += 28;

      // ========== Playing Positions Section ==========
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin, y, (contentWidth / 2) - 5, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.text(margin + 5, y + 6, "PLAYING POSITIONS");

      // Physical Performance header (right side)
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin + (contentWidth / 2) + 5, y, (contentWidth / 2) - 5, 8, "F");
      pdf.text(margin + (contentWidth / 2) + 10, y + 6, "PHYSICAL TESTS");

      y += 12;

      // Add pitch diagram on left - maintain aspect ratio (original 80x120)
      const pitchX = margin + 5;
      const pitchY = y;
      const pitchWidth = 75;
      const pitchHeight = pitchWidth * (120 / 80); // Maintain aspect ratio = 112.5mm

      let pitchBackground = document.getElementById("pitch_potions");
      pdf.addImage(pitchBackground, "PNG", pitchX, pitchY, pitchWidth, pitchHeight);
      this.addPlayingPostions(pdf, pitchX, pitchY, pitchWidth, pitchHeight);

      // Physical performance on right
      const physX = margin + (contentWidth / 2) + 10;
      let physY = y + 5;

      pdf.setFontSize(9).setFont(undefined, "normal");
      pdf.setTextColor(51, 51, 51);

      // Same metrics (and same latest-test source) as the profile page's
      // Physical Performance tiles, including height/weight from the test.
      const physTests = [
        { label: '10m Sprint', value: effectivePhysical.tenM_sprint_time, unit: 's' },
        { label: '30m Sprint', value: effectivePhysical.fortyM_sprint_time, unit: 's' },
        { label: 'CMJ', value: effectivePhysical.cuntermovement_jump, unit: 'cm' },
        { label: 'RSI', value: effectivePhysical.reactive_strength_index, unit: '' },
        { label: 'Fitness Test', value: effectivePhysical.thirty_15_intermittent_fitness_test, unit: '' },
        { label: 'Height', value: effectivePhysical.height, unit: 'cm' },
        { label: 'Weight', value: effectivePhysical.weight, unit: 'kg' },
      ];

      physTests.forEach((test) => {
        pdf.setFillColor(248, 250, 252);
        pdf.rect(physX - 5, physY - 4, 80, 12, "F");
        pdf.text(physX, physY + 3, test.label);
        const val = test.value ? `${test.value}${test.unit}` : 'N/A';
        pdf.setFont(undefined, "bold");
        pdf.text(physX + 70, physY + 3, val, { align: 'right' });
        pdf.setFont(undefined, "normal");
        physY += 15;
      });

      // Move y past the pitch (which is taller now: 112.5mm)
      y += pitchHeight + 5;
      console.log('Y position after pitch:', y, 'Page height:', pageHeight);

      // ========== Additional passport sections (new page) ==========
      // Page 1 is full with the pitch, so these flow onto a fresh page.
      pdf.addPage();
      y = margin + 10;
      y = this.addMedicalSection(pdf, y, margin, contentWidth, clubColor, pageHeight);
      y += 4;
      y = this.addPositionsPlayedSection(pdf, y, margin, contentWidth, clubColor, pageHeight);
      y += 4;
      y = this.addGrowthMaturationSection(pdf, y, margin, contentWidth, clubColor, pageHeight);

      // ========== GPS Performance Section (PTFC Only) ==========
      // Check if we need to move to page 2
      if (y > pageHeight - 100) {
        console.log('GPS section would overflow, moving to page 2');
        pdf.addPage();
        y = margin + 10;
      }

      console.log('GPS Section check - playerDataGpsSummary:', this.playerDataGpsSummary);
      console.log('GPS Section check - clubId:', this.userData?.clubId);
      if (this.playerDataGpsSummary && this.userData?.clubId === 'ptfc-2085XH') {
        console.log('Rendering GPS section...');

        // GPS section header
        pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
        pdf.rect(margin, y, contentWidth, 8, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(11).setFont(undefined, "bold");
        const matchCount = this.playerDataGpsSummary.matchSessionsCount || 0;
        const trainingCount = this.playerDataGpsSummary.trainingSessionsCount || 0;
        pdf.text(margin + 5, y + 6, `GPS PERFORMANCE (${matchCount} Match / ${trainingCount} Training Sessions)`);

        y += 12;
        pdf.setTextColor(51, 51, 51);

        const gpsMetrics = [
          { label: 'Total Distance', key: 'totalDistance', format: 'distance' },
          { label: 'Top Speed', key: 'maxSpeed', format: 'speed' },
          { label: 'Sprint Events', key: 'sprintEvents', format: 'number' },
          { label: 'High Intensity Running', key: 'highIntensityDistance', format: 'distance' },
          { label: 'Accelerations', key: 'accelerationEvents', format: 'number' },
          { label: 'Decelerations', key: 'decelerationEvents', format: 'number' },
          { label: 'Sprint Distance', key: 'sprintDistance', format: 'distance' },
          { label: 'Metres/Minute', key: 'metresPerMinute', format: 'mpm' },
        ];

        // Column widths
        const col1 = margin + 5;      // Metric name
        const col2 = margin + 55;     // Match Avg
        const col3 = margin + 85;     // Match Best
        const col4 = margin + 115;    // Training Avg
        const col5 = margin + 150;    // Training Best

        // GPS metrics table header
        pdf.setFillColor(241, 246, 249);
        pdf.rect(margin, y - 2, contentWidth, 12, "F");
        pdf.setFontSize(7).setFont(undefined, "bold");
        pdf.text(col1, y + 2, "Metric");

        // Match header
        pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
        pdf.setTextColor(clubColor.r, clubColor.g, clubColor.b);
        pdf.text(col2 + 10, y + 2, "MATCH", { align: 'center' });
        pdf.setTextColor(80, 80, 80);
        pdf.setFontSize(6);
        pdf.text(col2, y + 7, "Avg");
        pdf.text(col3, y + 7, "Best");

        // Training header
        pdf.setFontSize(7).setFont(undefined, "bold");
        pdf.setTextColor(100, 100, 100);
        pdf.text(col4 + 12, y + 2, "TRAINING", { align: 'center' });
        pdf.setTextColor(80, 80, 80);
        pdf.setFontSize(6);
        pdf.text(col4, y + 7, "Avg");
        pdf.text(col5, y + 7, "Best");

        y += 14;
        pdf.setFont(undefined, "normal");
        pdf.setFontSize(8);

        const matchData = this.playerDataGpsSummary.match || {};
        const trainingData = this.playerDataGpsSummary.training || {};

        gpsMetrics.forEach((metric, i) => {
          // Alternating row background
          if (i % 2 === 0) {
            pdf.setFillColor(252, 252, 253);
            pdf.rect(margin, y - 3, contentWidth, 8, "F");
          }

          pdf.setTextColor(51, 51, 51);
          pdf.text(col1, y + 2, metric.label);

          // Match data
          const mData = matchData[metric.key];
          if (mData) {
            let mAvgStr, mBestStr;
            switch (metric.format) {
              case 'distance':
                mAvgStr = this.formatGpsDistanceShort(mData.average);
                mBestStr = this.formatGpsDistanceShort(mData.best);
                break;
              case 'speed':
                mAvgStr = `${mData.average.toFixed(1)}`;
                mBestStr = `${mData.best.toFixed(1)}`;
                break;
              case 'mpm':
                mAvgStr = `${mData.average.toFixed(0)}`;
                mBestStr = `${mData.best.toFixed(0)}`;
                break;
              default:
                mAvgStr = `${Math.round(mData.average)}`;
                mBestStr = `${Math.round(mData.best)}`;
            }
            pdf.text(col2, y + 2, mAvgStr);
            pdf.setFont(undefined, "bold");
            pdf.setTextColor(clubColor.r, clubColor.g, clubColor.b);
            pdf.text(col3, y + 2, mBestStr);
            pdf.setFont(undefined, "normal");
          } else {
            pdf.setTextColor(150, 150, 150);
            pdf.text(col2, y + 2, '-');
            pdf.text(col3, y + 2, '-');
          }

          // Training data
          const tData = trainingData[metric.key];
          pdf.setTextColor(51, 51, 51);
          if (tData) {
            let tAvgStr, tBestStr;
            switch (metric.format) {
              case 'distance':
                tAvgStr = this.formatGpsDistanceShort(tData.average);
                tBestStr = this.formatGpsDistanceShort(tData.best);
                break;
              case 'speed':
                tAvgStr = `${tData.average.toFixed(1)}`;
                tBestStr = `${tData.best.toFixed(1)}`;
                break;
              case 'mpm':
                tAvgStr = `${tData.average.toFixed(0)}`;
                tBestStr = `${tData.best.toFixed(0)}`;
                break;
              default:
                tAvgStr = `${Math.round(tData.average)}`;
                tBestStr = `${Math.round(tData.best)}`;
            }
            pdf.text(col4, y + 2, tAvgStr);
            pdf.setFont(undefined, "bold");
            pdf.setTextColor(0, 128, 0);
            pdf.text(col5, y + 2, tBestStr);
            pdf.setFont(undefined, "normal");
          } else {
            pdf.setTextColor(150, 150, 150);
            pdf.text(col4, y + 2, '-');
            pdf.text(col5, y + 2, '-');
          }

          y += 8;
        });

        y += 5;
      }

      // ========== PAGE 2: Comments, Videos, Contact ==========
      pdf.addPage();
      y = margin + 5;

      // Coaches Comments Section
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.text(margin + 5, y + 6, "COACHES COMMENTS");

      y += 15;
      pdf.setTextColor(51, 51, 51);
      pdf.setFontSize(10).setFont(undefined, "normal");

      const comments = this.academyReport.coaches_comments || "No comments provided.";
      const commentLines = pdf.splitTextToSize(comments, contentWidth - 10);
      pdf.text(margin + 5, y, commentLines);

      y += commentLines.length * 5 + 15;

      // Playing Videos Section
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.text(margin + 5, y + 6, "PLAYING VIDEOS");

      y += 15;
      pdf.setTextColor(51, 51, 51);
      pdf.setFontSize(10).setFont(undefined, "normal");

      if (this.playerCvData.videos && this.playerCvData.videos.length > 0) {
        let videoIcon = document.getElementById("video-icon");
        this.playerCvData.videos.forEach((item) => {
          if (y > pageHeight - 30) {
            pdf.addPage();
            y = margin + 10;
          }
          pdf.addImage(videoIcon, "PNG", margin + 5, y - 5, 8, 8);
          pdf.textWithLink(item.video_name, margin + 18, y, { url: item.video_url });
          y += 10;
        });
      } else {
        pdf.text(margin + 5, y, "No videos available for this player.");
        y += 10;
      }

      y += 10;

      // Contact Details Section
      pdf.setFillColor(clubColor.r, clubColor.g, clubColor.b);
      pdf.rect(margin, y, contentWidth, 8, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(11).setFont(undefined, "bold");
      pdf.text(margin + 5, y + 6, "CONTACT DETAILS");

      y += 15;
      pdf.setTextColor(51, 51, 51);
      pdf.setFontSize(10).setFont(undefined, "normal");

      pdf.text(margin + 5, y, `Email: ${this.selectedUserData.email || 'N/A'}`);
      y += 7;
      pdf.text(margin + 5, y, `Phone: ${this.selectedUserData.phone || 'N/A'}`);
      y += 7;
      if (this.selectedUserData.parent_name) {
        pdf.text(margin + 5, y, `Parent/Guardian: ${this.selectedUserData.parent_name}`);
      }

      // Footer on both pages
      const addFooter = (pageNum) => {
        pdf.setPage(pageNum);
        pdf.setFillColor(241, 246, 249);
        pdf.rect(0, pageHeight - 15, pageWidth, 15, "F");
        pdf.setFontSize(8).setFont(undefined, "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(margin, pageHeight - 6, `Generated: ${moment().format('DD MMM YYYY')}`);
        pdf.text(pageWidth - margin, pageHeight - 6, `Page ${pageNum}`, { align: 'right' });
      };

      const totalPages = pdf.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        addFooter(p);
      }

      console.log('PDF generation complete, previewContainer:', previewContainer);
      return { pdfData: pdf, previewLink: previewContainer };
    },
    async updateTheAcademyReport(id) {
      id !== null ? this.editAcademyReport() : this.saveAcademyReport();
    },
    async editAcademyReport() {
      this.academyReport.override_extra = JSON.stringify(this.extraOverrides);
      const data = {
        action: "editAcademyReport",
        academyReport: this.academyReport,
      };
      const responce = await AcademyReport.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.populateAcademyReport();
      }
      this.closeEditModal();
    },
    async saveAcademyReport() {
      this.academyReport.player_id = this.selectedUserValue;
      this.academyReport.override_extra = JSON.stringify(this.extraOverrides);
      const data = {
        action: "saveAcademyReport",
        academyReport: this.academyReport,
      };
      const responce = await AcademyReport.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.populateAcademyReport();
        this.closeEditModal();
      }
    },
    async populateAcademyReport() {
      const data = {
        action: "populateAcademyReport",
        userId: this.selectedUserValue,
      };
      const responce = await AcademyReport.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200 && responce.data.id !== undefined) {
        // Map response to academyReport, handling null values
        this.academyReport = {
          id: responce.data.id,
          player_id: responce.data.player_id,
          seasion: responce.data.seasion,
          average_score: responce.data.average_score || 0,
          coaches_comments: responce.data.coaches_comments || "",
          // Player Details overrides
          override_dob: responce.data.override_dob || null,
          override_age_group: responce.data.override_age_group || null,
          override_joined_date: responce.data.override_joined_date || null,
          override_height: responce.data.override_height || null,
          override_weight: responce.data.override_weight || null,
          override_position: responce.data.override_position || null,
          // Season Performance overrides - handle null, undefined, and empty strings
          override_appearances: responce.data.override_appearances != null && responce.data.override_appearances !== '' ? parseInt(responce.data.override_appearances) : null,
          override_goals: responce.data.override_goals != null && responce.data.override_goals !== '' ? parseInt(responce.data.override_goals) : null,
          override_assists: responce.data.override_assists != null && responce.data.override_assists !== '' ? parseInt(responce.data.override_assists) : null,
          override_game_time: responce.data.override_game_time != null && responce.data.override_game_time !== '' ? parseInt(responce.data.override_game_time) : null,
          override_self_rating: responce.data.override_self_rating || null,
          override_extra: responce.data.override_extra || null,
        };
        // Rehydrate the newer-section overrides from the JSON blob.
        this.extraOverrides = this.parseExtraOverrides(
          responce.data.override_extra,
        );
        this.loadPlayerPhysicalPerformanceData();
        // So edit-modal placeholders + the playing up/down value are ready.
        this.loadBioAgeData();
        this.loadPlayersOtherTeams();
      }
    },
    // Returns a full extraOverrides object (defaults + any saved values).
    parseExtraOverrides(raw) {
      const defaults = {
        nationality: null,
        second_nationality: null,
        country_of_birth: null,
        playing_up_down: null,
        medical: null,
        tenm_sprint: null,
        thirtym_sprint: null,
        cmj: null,
        rsi: null,
        fitness_test: null,
        chronological_age: null,
        bio_age: null,
        bio_age_difference: null,
        estimated_adult_height: null,
        adult_height_achieved: null,
      };
      if (!raw) return defaults;
      try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        return { ...defaults, ...(parsed || {}) };
      } catch (e) {
        return defaults;
      }
    },
    /**
     * Clear all override fields
     */
    clearOverrides() {
      this.academyReport.override_dob = null;
      this.academyReport.override_age_group = null;
      this.academyReport.override_joined_date = null;
      this.academyReport.override_height = null;
      this.academyReport.override_weight = null;
      this.academyReport.override_position = null;
      this.academyReport.override_appearances = null;
      this.academyReport.override_goals = null;
      this.academyReport.override_assists = null;
      this.academyReport.override_game_time = null;
      this.academyReport.override_self_rating = null;
      this.extraOverrides = this.parseExtraOverrides(null);
    },
    /**
     * Get effective value (override or actual)
     */
    getEffectivePlayerDetails() {
      return {
        dob: this.academyReport.override_dob || this.selectedUserData.dob,
        age_group: this.academyReport.override_age_group || this.selectedUserData.age_group,
        joined_date: this.academyReport.override_joined_date || this.selectedUserData.joined_date,
        height: this.academyReport.override_height || this.playerPhysicalPerformanceData.height,
        weight: this.academyReport.override_weight || this.playerPhysicalPerformanceData.weight,
        position: this.academyReport.override_position || this.selectedUserData.position,
      };
    },
    /**
     * Get effective season performance (override or actual)
     * Ensures all values are valid numbers or appropriate defaults
     */
    getEffectiveSeasonPerformance() {
      // Helper to safely get a numeric value (handles strings, numbers, null, undefined, NaN)
      const safeNumber = (val, fallback = 0) => {
        if (val === null || val === undefined || val === '') return fallback;
        const num = Number(val);
        return isNaN(num) ? fallback : num;
      };

      // Helper to check if a value is valid (not null, undefined, empty, or NaN)
      const isValidOverride = (val) => {
        if (val === null || val === undefined || val === '') return false;
        if (typeof val === 'number' && isNaN(val)) return false;
        // Also check if it's a string 'NaN'
        if (val === 'NaN') return false;
        return true;
      };

      // Helper to get override or actual value (preserving 0 as valid)
      const getVal = (override, actual) => {
        if (isValidOverride(override)) {
          return safeNumber(override);
        }
        return safeNumber(actual);
      };

      const appearances = getVal(
        this.academyReport.override_appearances,
        this.gamePerformanceData?.gamesPlayed
      );

      const goals = getVal(
        this.academyReport.override_goals,
        this.gamePerformanceData?.goals
      );

      const assists = getVal(
        this.academyReport.override_assists,
        this.gamePerformanceData?.assists
      );

      const gameTime = getVal(
        this.academyReport.override_game_time,
        this.gamePerformanceData?.timeOnPitch
      );

      // Self rating can be 'N/A' or a number
      let selfRating = this.academyReport.override_self_rating;
      if (!isValidOverride(selfRating)) {
        selfRating = this.gamePerformanceData?.player_self_rating;
      }
      if (selfRating === null || selfRating === undefined || selfRating === '') {
        selfRating = 'Not rated';
      } else {
        const num = Number(selfRating);
        if (!isNaN(num)) {
          selfRating = num.toFixed(1);
        }
      }

      return {
        appearances,
        goals,
        assists,
        gameTime,
        selfRating,
      };
    },
    // Returns the override for an extraOverrides key when set (non-empty),
    // otherwise the player's actual value.
    effExtra(key, actual) {
      const v = this.extraOverrides && this.extraOverrides[key];
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
      return actual;
    },
    getEffectiveIdentity() {
      return {
        nationality: this.effExtra("nationality", this.selectedUserData.nationality),
        second_nationality: this.effExtra(
          "second_nationality",
          this.selectedUserData.second_nationality,
        ),
        country_of_birth: this.effExtra(
          "country_of_birth",
          this.selectedUserData.country_of_birth,
        ),
        playing_up_down: this.effExtra(
          "playing_up_down",
          this.playingUpDownCompact(),
        ),
      };
    },
    getEffectiveMedical() {
      return this.effExtra("medical", this.selectedUserData.medical);
    },
    getEffectivePhysical() {
      const p = this.playerPhysicalPerformanceData || {};
      return {
        tenM_sprint_time: this.effExtra("tenm_sprint", p.tenM_sprint_time),
        fortyM_sprint_time: this.effExtra("thirtym_sprint", p.fortyM_sprint_time),
        cuntermovement_jump: this.effExtra("cmj", p.cuntermovement_jump),
        reactive_strength_index: this.effExtra("rsi", p.reactive_strength_index),
        thirty_15_intermittent_fitness_test: this.effExtra(
          "fitness_test",
          p.thirty_15_intermittent_fitness_test,
        ),
        height: this.academyReport.override_height || p.height,
        weight: this.academyReport.override_weight || p.weight,
      };
    },
    getEffectiveGrowth() {
      const b = this.bioAgeData || {};
      const isSet = (k) => {
        const v = this.extraOverrides[k];
        return v !== null && v !== undefined && String(v).trim() !== "";
      };
      const anyOverride =
        isSet("chronological_age") ||
        isSet("bio_age") ||
        isSet("bio_age_difference") ||
        isSet("estimated_adult_height") ||
        isSet("adult_height_achieved");
      // Nothing overridden and no calculated data -> no section content.
      if (!anyOverride && (b.bioAge === null || b.bioAge === undefined)) {
        return null;
      }
      return {
        decimalAge: this.effExtra("chronological_age", b.decimalAge),
        bioAge: this.effExtra("bio_age", b.bioAge),
        bioAgeDifference: this.effExtra("bio_age_difference", b.bioAgeDifference),
        childEstimatedHeight: this.effExtra(
          "estimated_adult_height",
          b.childEstimatedHeight,
        ),
        childEstimatedHeightAchieved: this.effExtra(
          "adult_height_achieved",
          b.childEstimatedHeightAchieved,
        ),
      };
    },
    formatDateForHuman(date) {
      return date && moment(date).isValid()
        ? moment(date).format("MMMM Do YYYY")
        : "Invalid date";
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;

  &.modal-fullscreen {
    .modal-dialog {
      max-width: 95%;
      max-height: 120vh;
    }
  }
}

.modal-dialog {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 120vh;
  overflow: hidden;

  &.modal-lg {
    max-width: 800px;
  }

  &.modal-xl {
    max-width: 1140px;
  }
}

.modal-content {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: scroll;
}

.modal-header {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fa;

  h3 {
    margin: 0;
    font-size: 1.25rem;
  }
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  opacity: 0.5;
  cursor: pointer;

  &:hover {
    opacity: 0.75;
  }
}

.modal-body {
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
}

.modal-container {
  margin-top: 1rem;
  width: 100%;
  // Fill the fullscreen modal vertically so the whole A4 page is visible in
  // the preview rather than only the top half.
  height: 85vh;

  iframe {
    border: 0;
    width: 100%;
    height: 85vh;
  }
}

.performance-report {
  img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }
}

.form-control {
  width: 100%;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition:
    border-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  &:focus {
    color: #495057;
    background-color: #fff;
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }
}

.player-passport-buttons {
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  background: #f9fafb;
  border: 1px solid #eee;
  border-radius: 10px;

  .passport-title {
    margin: 0 0 0.75rem;
  }

  .player-passport {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0;

    .view-passport {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 0.85rem;

      &:hover {
        opacity: 0.8;
      }

      .passport-caption {
        font-size: 0.85rem;
        color: #4b5563;
        line-height: 1.3;
      }
    }
  }

  img#passport-icon {
    width: 4rem;
    margin-right: 0;
    background: white;
  }
}

/* Loading overlay styles */
.modal-container.performance-report {
  position: relative;
  min-height: 400px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  border-radius: 4px;
}

.loading-container {
  text-align: center;
  padding: 2rem;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e9ecef;
  border-top: 4px solid var(--primary-color, #007bff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-status {
  font-size: 1rem;
  color: #495057;
  margin-bottom: 1rem;
  font-weight: 500;
}

.progress-bar {
  width: 200px;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color, #007bff), var(--primary-color, #0056b3));
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Button spinner animation */
.spin-animation {
  display: inline-block;
  animation: spin 1s linear infinite;
}

/* Override Section Styles */
.override-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 0.25rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--primary-color, #eb2144);
}

.section-hint {
  font-size: 0.8rem;
  color: #6c757d;
  margin: 0 0 1rem 0;
  font-style: italic;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.form__item {
  display: flex;
  flex-direction: column;
}

.form__label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.25rem;
}

.buttons-row {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

/* Responsive adjustments for edit modal */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .buttons-row {
    flex-direction: column;

    button {
      width: 100%;
    }
  }
}
</style>
